import { useContext, useEffect, useState } from 'react'
import { UserDetailsContext } from '../context/user-details/user-details'
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import SuperAdminService from '../shared/services/super-admin/super-admin.service';
import { ReportPeriodContext } from '../context/report-periods/report-periods';
import VehicleService from '../shared/services/vehicle/vehicle.service';
import LocationService from '../shared/services/location/location.service';
import GroupService from '../shared/services/group/group.service';
import SubsidiaryService from '../shared/services/subsidiary/subsidiary.service';
import ProjectService from '../shared/services/project/project.service';
import ContactSupplierService from '../shared/services/contact-supplier/contact-supplier.service';
import gaiaTheme from './theme';
import { usePdfExportMode } from '../hooks/use-pdf-export-mode';

// leftOffset: width in px of the left rail chrome. The rail's Drawer paints above this
// fixed banner, so the banner starts at the rail's right edge and centres its content in
// the visible area. 0 on mobile / wherever the rail is hidden.
export const WarningBanner = ({ leftOffset = 0 }: { leftOffset?: number }) => {
  const navigate = useNavigate()
  const location = useLocation();
  const pdfExportMode = usePdfExportMode();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const superAdminService = new SuperAdminService();
  const vehicleService = VehicleService.getInstance();
  const locationService = LocationService.getInstance();
  const groupService = GroupService.getInstance();
  const subsidiaryService = SubsidiaryService.getInstance();
  const projectService = ProjectService.getInstance();
  const contactSupplierService = ContactSupplierService.getInstance();
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [organisationName, setOrganisationName] = useState<string | undefined>()

  useEffect(() => {
    setShowBanner(!!userDetailsContext.userDetails?.isLoggedInAsCustomer);
    setOrganisationName(userDetailsContext.userDetails?.organisation?.name);
  }, [location.pathname, userDetailsContext.userDetails]);

  const resetUserOrganisation = () => {
    reportPeriodContext.clearData();
    vehicleService.clearData();
    groupService.clearData();
    locationService.clearData();
    subsidiaryService.clearData();
    projectService.clearData();
    contactSupplierService.clearData();
    superAdminService.resetOrganisation()
      .then(() => {
        userDetailsContext.forceUpdateUserDetails();
        navigate('/super-admin');
      })
  }

  if (pdfExportMode) return null;
  if (!showBanner) return null;

  return (
    <Box sx={{
      height: isMobileMode ? '105px' : '48px',
      width: `calc(100% - ${leftOffset}px)`,
      backgroundColor: 'rgba(255, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      mt: isMobileMode ? 13 : 8,
      px: 2,
      borderBottom: '1px solid',
      borderColor: 'divider',
      position: 'fixed',
      top: 0,
      left: `${leftOffset}px`,
      zIndex: (theme) => theme.zIndex.appBar + 1,
      flexDirection: isMobileMode ? 'column' : 'row'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: isMobileMode ? '80vw' : '60vw', overflow: 'hidden' }} aria-live='polite'>
        <Typography variant="body1" sx={{ color: 'common.white', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          You are currently logged in to organisation: {organisationName}
        </Typography>
      </Box>
      <Button
        sx={{
          ml: isMobileMode ? 0 : 1,
          backgroundColor: '#f44336',
          color: 'common.white',
          '&:hover': {
            backgroundColor: '#d32f2f'
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#d32f2f'
          }
        }}
        variant='contained'
        onClick={resetUserOrganisation}
      >
        <Typography>
          Exit organisation
        </Typography>
      </Button>
    </Box>
  )
}